import { getAllowedSportCodes } from "./common/config-util";
import { EventStatusCode, getByCode } from "../sports/common/event-status";
import ApiService from "./api-service";

class GamedayApiService extends ApiService {
    constructor() {
        super();
    }

    setGamedayApiClient(gamedayApiClient) {
        this.remoteDataStrategy.setGamedayApiClient(gamedayApiClient);
    }

    async getEventByExternalId(eventId) {
        return this.dataStrategy.getEventByExternalId(eventId);
    }

    async getTeam(externalTeamId) {
        return this.dataStrategy.getTeam(externalTeamId);
    }

    async getSeasonTeams(seasonId, externalTeamIds) {
        return this.dataStrategy.getSeasonTeams(seasonId, externalTeamIds);
    }

    async getSeasons(sportCodes = []) {
        return this.dataStrategy.getSeasons(
            sportCodes?.length ? sportCodes : getAllowedSportCodes()
        );
    }

    async getSeason(externalSeasonId) {
        return this.dataStrategy.getSeason(externalSeasonId);
    }

    async getUpcomingEvents(
        seasonId,
        mergeOptions,
        filterOptions,
        pagingOptions
    ) {
        return this.dataStrategy.getEventsByCriteria(
            seasonId,
            mergeOptions,
            Object.assign({}, filterOptions, {
                statusCode: getByCode(EventStatusCode.NOT_STARTED)
            }),
            pagingOptions
        );
    }

    async getCompletedEvents(
        seasonId,
        mergeOptions,
        filterOptions,
        pagingOptions
    ) {
        return this.dataStrategy.getEventsByCriteria(
            seasonId,
            mergeOptions,
            Object.assign({}, filterOptions, {
                statusCode: getByCode(EventStatusCode.FINAL)
            }),
            pagingOptions
        );
    }

    async getInProgressEvents(
        seasonId,
        mergeOptions,
        filterOptions,
        pagingOptions
    ) {
        return this.dataStrategy.getEventsByCriteria(
            seasonId,
            mergeOptions,
            Object.assign({}, filterOptions, {
                statusCode: getByCode(EventStatusCode.IN_PROGRESS)
            }),
            pagingOptions
        );
    }

    async getLastWeekEvents(sportCodes = []) {
        return this.dataStrategy.getLastWeekEvents(
            sportCodes?.length ? sportCodes : getAllowedSportCodes()
        );
    }

    async getNextWeekEvents(sportCodes = []) {
        return this.dataStrategy.getNextWeekEvents(
            sportCodes?.length ? sportCodes : getAllowedSportCodes()
        );
    }

    async saveOrUpdatePlayer(player) {
        return this.dataStrategy.saveOrUpdatePlayer(player);
    }

    async saveOrUpdatePlayers(externalTeamId, players) {
        return this.dataStrategy.saveOrUpdatePlayers(externalTeamId, players);
    }

    async saveOrUpdateEvent(jsonEvent) {
        return this.dataStrategy.saveOrUpdateEvent(jsonEvent);
    }

    async getUserTeams(seasonId) {
        return this.dataStrategy.getUserTeams(seasonId);
    }

    async getOrCreateEventTeams(event) {
        return this.dataStrategy.getOrCreateEventTeams(event);
    }

    async getOrCreateSeason(jsonSeason) {
        return this.dataStrategy.getOrCreateSeason(jsonSeason);
    }

    async getOrCreateTeam(jsonTeam) {
        return this.dataStrategy.getOrCreateTeam(jsonTeam);
    }

    async canEditGame(eventId) {
        return this.dataStrategy.canEditGame(eventId);
    }

    async getTimeZones() {
        return this.dataStrategy.getTimeZones();
    }

    async getPlayers(externalTeamId, includeDeleted = false) {
        return this.dataStrategy.getPlayers(externalTeamId, includeDeleted);
    }

    async getEventsByDateSportCodeAndStatusCode(date, sportCode, statusCodes) {
        return this.dataStrategy.getEventsByDateSportCodeAndStatusCode(
            date,
            sportCode,
            statusCodes
        );
    }

    async saveEventData(remoteEventId, eventData) {
        return this.dataStrategy.saveEventData(remoteEventId, eventData);
    }

    async fetchEventData(remoteEventId) {
        return this.dataStrategy.fetchEventData(remoteEventId);
    }

    async saveBoxscore(jsonBoxscore) {
        return this.dataStrategy.saveBoxscore(jsonBoxscore);
    }

    async getSeasonSettings(seasonId) {
        return this.dataStrategy.getSeasonSettings(seasonId);
    }

    async saveSeasonSettings(seasonId, settings) {
        return this.dataStrategy.saveSeasonSettings(seasonId, settings);
    }

    async syncTeamPlayers(team, externalEventId) {
        return this.dataStrategy.syncTeamPlayers(team, externalEventId);
    }
}

export default new GamedayApiService();
